import { UseFormReturn, SubmitHandler } from 'react-hook-form';

import { Form, FormField } from 'components/form';
import { formatDateShort } from 'utils/formatters';
import { GlobalScrollLock } from 'components/GlobalScrollLock';

import { CompletionFormValues } from '../index';
import * as styled from './styles';

type Props = {
  booking: Kudable.BookingViewData['booking'];
  form: UseFormReturn<CompletionFormValues>;
  onSubmitCode: SubmitHandler<CompletionFormValues>;
  submitError: string | null;
  confirmed: boolean;
  acceptingCounter: boolean;
  cancelling: boolean;
  showCancelConfirm: boolean;
  onAcceptCounter: () => void;
  onRequestCancel: () => void;
  onConfirmCancel: () => void;
  onDismissCancel: () => void;
};

const CODE_MAX_LENGTH = 4;

export const BookingScene = ({
  booking,
  form,
  onSubmitCode,
  submitError,
  confirmed,
  acceptingCounter,
  cancelling,
  showCancelConfirm,
  onAcceptCounter,
  onRequestCancel,
  onConfirmCancel,
  onDismissCancel,
}: Props) => {
  const isCompleted = !!booking.completed_at || confirmed;
  const isCancelled = booking.status === 'cancelled';
  const hasCounterOffer =
    booking.price_negotiation_status === 'provider_countered';
  const priceAgreed = booking.price_negotiation_status === 'agreed';

  return (
    <div>
      <styled.TopBar>
        <styled.TopBarInner>
          <styled.Logo href="/">
            <styled.LogoMark>K</styled.LogoMark>
            <styled.LogoWordmark>kudable</styled.LogoWordmark>
          </styled.Logo>
          <styled.PromoCta href="/">✨ Get your own profile</styled.PromoCta>
        </styled.TopBarInner>
      </styled.TopBar>

      <styled.Container>
        <styled.Card>
          <styled.Header>
            <styled.HeaderTitle>Your Booking</styled.HeaderTitle>
            <styled.ProviderName>
              with {booking.provider_name}
            </styled.ProviderName>
            {booking.service && (
              <styled.ServiceName>{booking.service.name}</styled.ServiceName>
            )}
            <styled.DateText>
              {formatDateShort(booking.booked_at)}
            </styled.DateText>
          </styled.Header>

          <styled.StatusRow>
            <StatusBadge status={booking.status} isCompleted={isCompleted} />
          </styled.StatusRow>

          {isCancelled && (
            <styled.CancelledBox>
              <styled.CancelledTitle>Booking Cancelled</styled.CancelledTitle>
              <styled.CancelledHint>
                This booking has been cancelled and the slot is now available.
              </styled.CancelledHint>
            </styled.CancelledBox>
          )}

          {hasCounterOffer && !isCompleted && !isCancelled && (
            <styled.CounterBox>
              <styled.CounterTitle>
                Price offer from {booking.provider_name}
              </styled.CounterTitle>
              <styled.CounterAmount>
                {booking.currency} {booking.provider_counter_price}
              </styled.CounterAmount>
              {booking.client_suggested_price && (
                <styled.CounterOriginal>
                  Your original request: {booking.currency}{' '}
                  {booking.client_suggested_price}
                </styled.CounterOriginal>
              )}
              <styled.CounterActions>
                <styled.AcceptBtn
                  onClick={onAcceptCounter}
                  disabled={acceptingCounter}
                >
                  {acceptingCounter ? 'Accepting…' : 'Accept this price'}
                </styled.AcceptBtn>
                <styled.RejectBtn onClick={onRequestCancel}>
                  Reject price
                </styled.RejectBtn>
              </styled.CounterActions>
              {submitError && (
                <styled.ErrorText>{submitError}</styled.ErrorText>
              )}
            </styled.CounterBox>
          )}

          {priceAgreed && !isCompleted && !isCancelled && (
            <styled.AgreedBox>
              <styled.AgreedText>
                ✓ Price agreed: {booking.currency} {booking.agreed_price}
              </styled.AgreedText>
            </styled.AgreedBox>
          )}

          {isCompleted ? (
            <styled.CompletedBox>
              <styled.CompletedIcon>✓</styled.CompletedIcon>
              <styled.CompletedTitle>Job Confirmed!</styled.CompletedTitle>
              <styled.CompletedHint>
                This booking has been marked complete. Your provider will
                receive the "Job Confirmed" badge on any review for this job.
              </styled.CompletedHint>
            </styled.CompletedBox>
          ) : !isCancelled && booking.status === 'accepted' ? (
            <Form form={form} onSubmit={onSubmitCode}>
              <styled.CodeSection>
                <styled.CodeSectionTitle>
                  Enter your completion code
                </styled.CodeSectionTitle>
                <styled.CodeSectionHint>
                  Enter the 4-digit code from your booking confirmation to
                  confirm the job is done.
                </styled.CodeSectionHint>
                <FormField
                  name="code"
                  inputMode="numeric"
                  maxLength={CODE_MAX_LENGTH}
                  placeholder="0000"
                />
                {submitError && !hasCounterOffer && (
                  <styled.ErrorText>{submitError}</styled.ErrorText>
                )}
                <styled.SubmitBtn
                  type="submit"
                  disabled={form.formState.isSubmitting}
                >
                  {form.formState.isSubmitting
                    ? 'Confirming…'
                    : 'Confirm job done'}
                </styled.SubmitBtn>
              </styled.CodeSection>
            </Form>
          ) : null}
        </styled.Card>
      </styled.Container>

      {showCancelConfirm && (
        <styled.Overlay>
          <GlobalScrollLock />
          <styled.Dialog>
            <styled.DialogTitle>
              {booking.recurrence_rule === 'weekly'
                ? 'Cancel this recurring series?'
                : 'Cancel this booking?'}
            </styled.DialogTitle>
            <styled.DialogBody>
              {booking.recurrence_rule === 'weekly'
                ? 'This is a weekly recurring booking. Cancelling will end the entire series — every future occurrence will be freed up. Past occurrences are unaffected.'
                : 'Rejecting this price offer will cancel your booking. The slot will be freed and you will need to rebook if you change your mind.'}
            </styled.DialogBody>
            <styled.DialogActions>
              <styled.DialogCancelBtn onClick={onDismissCancel}>
                Keep booking
              </styled.DialogCancelBtn>
              <styled.DialogConfirmBtn
                onClick={onConfirmCancel}
                disabled={cancelling}
              >
                {cancelling
                  ? 'Cancelling…'
                  : booking.recurrence_rule === 'weekly'
                    ? 'Yes, cancel series'
                    : 'Yes, cancel booking'}
              </styled.DialogConfirmBtn>
            </styled.DialogActions>
          </styled.Dialog>
        </styled.Overlay>
      )}
    </div>
  );
};

const StatusBadge = ({
  status,
  isCompleted,
}: {
  status: string;
  isCompleted: boolean;
}) => {
  if (isCompleted)
    return <styled.Badge $type="completed">Completed</styled.Badge>;
  if (status === 'accepted')
    return <styled.Badge $type="accepted">Accepted</styled.Badge>;
  if (status === 'pending')
    return <styled.Badge $type="pending">Pending</styled.Badge>;
  if (status === 'rejected')
    return <styled.Badge $type="rejected">Rejected</styled.Badge>;
  if (status === 'cancelled')
    return <styled.Badge $type="cancelled">Cancelled</styled.Badge>;
  return null;
};
